import useCookie from 'hooks/useCookie';
import { useCallback, useMemo } from 'react';

export const COOKIE_CONSENT_NAME = 'cookie-consent';

export type CookieCategory = 'ads' | 'store' | 'scan' | 'geo' | 'select' | 'analytics';

export const ALL_CATEGORIES: CookieCategory[] = [
  'ads',
  'store',
  'scan',
  'geo',
  'select',
  'analytics',
];

export type CookieConsentData = {
  settings: CookieCategory[];
  consentDate: number;
};

export type CookieCategorySelections = {
  adsAllowed: boolean;
  storeAllowed: boolean;
  scanAllowed: boolean;
  geoAllowed: boolean;
  selectAllowed: boolean;
  analyticsAllowed: boolean;
};

function parse(raw: string | undefined): CookieConsentData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CookieConsentData;
    if (Array.isArray(parsed?.settings)) return parsed;
    return null;
  } catch {
    return null;
  }
}

export default function useGranularCookieConsent() {
  const [raw, setRaw] = useCookie(COOKIE_CONSENT_NAME);
  const data = useMemo(() => parse(raw), [raw]);
  const hasMadeChoice = data !== null;

  const adsAllowed = data?.settings.includes('ads') ?? false;
  const storeAllowed = data?.settings.includes('store') ?? false;
  const scanAllowed = data?.settings.includes('scan') ?? false;
  const geoAllowed = data?.settings.includes('geo') ?? false;
  const selectAllowed = data?.settings.includes('select') ?? false;
  const analyticsAllowed = data?.settings.includes('analytics') ?? false;

  const persist = useCallback(
    (settings: CookieCategory[]) => {
      setRaw(JSON.stringify({ settings, consentDate: Date.now() }));
    },
    [setRaw],
  );

  const acceptAll = useCallback(() => persist([...ALL_CATEGORIES]), [persist]);
  const rejectAll = useCallback(() => persist([]), [persist]);

  const saveSelected = useCallback(
    (selections: CookieCategorySelections) => {
      const settings: CookieCategory[] = [];
      if (selections.adsAllowed) settings.push('ads');
      if (selections.storeAllowed) settings.push('store');
      if (selections.scanAllowed) settings.push('scan');
      if (selections.geoAllowed) settings.push('geo');
      if (selections.selectAllowed) settings.push('select');
      if (selections.analyticsAllowed) settings.push('analytics');
      persist(settings);
    },
    [persist],
  );

  return {
    hasMadeChoice,
    adsAllowed,
    storeAllowed,
    scanAllowed,
    geoAllowed,
    selectAllowed,
    analyticsAllowed,
    acceptAll,
    rejectAll,
    saveSelected,
  };
}
